import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// ImportPlain - a WhatsApp database that needs NO KEY: an iPhone's `ChatStorage.sqlite` (or an Android
// `msgstore.db` somebody already unlocked). Every rule and every sentence of that branch of the
// import step lives here; `AppPlainSniff` is the transport that reads the bytes off the device.
//
// ⚠ IT HAS NO HABITAT (no `window`, no `FileReader`), so the Bridge suite can import it and check
// the page's verdict against the service's (`ArchiveFormat.KindOf`) on the same bytes.
//
// ==================================================================================================
// !! ⭐ WHY THE PAGE LOOKS INSIDE THE FILE BEFORE SENDING IT
// ==================================================================================================
//
// An iPhone has no `.crypt15`. WhatsApp for iOS keeps a PLAINTEXT SQLite database, and the only way
// a subscriber reaches it is a Finder/iTunes backup on a computer - a folder of thousands of files
// with hashed names and no extensions. So the name proves nothing (the right file is called
// `7c7fba66680ef796b916b067077cc246adacf01d`), and the wrong picks are all plausible: the backup's
// own index (`Manifest.db`, also SQLite), a `.plist`, a file out of an ENCRYPTED backup (random
// bytes), a zip.
//
// ⛔ THE SERVICE'S REFUSAL NEVER REACHES THE SUBSCRIBER. The import runs on the next refresh cycle,
// minutes after this page said "uploaded"; a file it cannot read is a line in the operator's log.
// So the sentence has to be said HERE, before hundreds of megabytes are sent for nothing:
// `HeadVerdict` reads the first 100 bytes (is it SQLite at all?), and `PlainSchemaWalk` reads the
// database's own table list - the `sqlite_master` b-tree, a handful of pages - for WhatsApp's tables.
// The service tells the kinds apart the same way, by SCHEMA, never by name.
//
// ⭐ THE FILE IS NEVER LOADED INTO MEMORY. Only those few pages are read; the upload streams it from
// disk in pieces (`AppDropboxUpload` takes the `Blob`). That is why the 250 MB ceiling of the
// `.crypt15` path - which must hold ciphertext, plaintext and inflated database at once - does not
// apply here, and the service's own ceiling does (`MaxBytes`).
//
// ⚠ WHAT A SECOND CONSUMER CHANGES: another plaintext format is one more mark in `PlainSchemaWalk.
// Marks` and one more `Kind`. The walk itself knows nothing about WhatsApp.

/** What the first bytes of a chosen file say. `Why` is the sentence when it cannot be used. */
export class PlainHead {
    public Ok: boolean = false;
    public Why: string = "";
    /** Bytes per database page - what `PlainSchemaWalk` slices by. */
    public PageSize: number = 0;
}

/** The answer about a chosen file: usable (`Kind` says which phone wrote it) or a sentence. */
export class PlainVerdict {
    public Ok: boolean = false;
    /** `ImportPlainRules.Iphone` / `Android`, or "" when it is not usable. */
    public Kind: string = "";
    public Why: string = "";
}

export class ImportPlainRules {
    public static readonly Iphone: string = "iphone";
    public static readonly Android: string = "android";

    /**
     * What an iPhone backup calls WhatsApp's database, and the name the file has inside the backup
     * folder: the SHA-1 of that string (a test computes it). The backup stores each file under the
     * first two characters of its hash, so it sits in the folder `7c`.
     */
    public static readonly BackupDomainPath: string = "AppDomainGroup-group.net.whatsapp.WhatsApp.shared-ChatStorage.sqlite";
    public static readonly HashedName: string = "7c7fba66680ef796b916b067077cc246adacf01d";

    /** How much of the file `HeadVerdict` needs: SQLite's own header. */
    public static readonly HeadBytes: number = 100;

    /**
     * The largest database the service accepts in one file - `ImportPullRules.MaxBytes`, and a Bridge
     * test holds the two equal. ⚠ Not a browser-memory limit: this file is streamed, never loaded.
     */
    public static readonly MaxBytes: number = 2 * 1024 * 1024 * 1024;

    /** "SQLite format 3" and its terminating zero - the first sixteen bytes of every SQLite file. */
    private static readonly Magic: number[] = [0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61,
        0x74, 0x20, 0x33, 0x00];

    /**
     * Whether a chosen file is looked INTO rather than judged by its name. Everything is - except the
     * encrypted Android kinds, whose names `ImportGate` already answers with a sentence of their own.
     */
    public static NeedsSniff = (Name: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return /\.(crypt\d+|mcrypt1)$/i.test(nz(Name).trim()) === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportPlainRules.NeedsSniff");
            return false;
        }
    };

    /** Why a file of this size cannot be sent, or "". */
    public static SizeRefusal = (Bytes: number): string => {
        if (getCoreUtils().KErrorMode === true) { return "WACommander cannot read a backup now."; }
        try {
            const N: number = Number(Bytes);
            if (isNaN(N) === true || N <= 0) { return ImportPlainRules.EmptyWhy; }
            if (N > ImportPlainRules.MaxBytes) {
                return "That file is larger than 2 GB, which is more than WACommander accepts in one file. A WhatsApp"
                    + " message database is normally far smaller - check it is " + ImportPlainRules.HashedName + ".";
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportPlainRules.SizeRefusal");
            return "That file could not be checked.";
        }
    };

    public static readonly EmptyWhy: string = "That file is empty. If it was just copied, give it a moment to finish"
        + " and choose it again.";

    /**
     * What the first `HeadBytes` of the file say.
     *
     * ⚠ EACH WRONG PICK GETS ITS OWN SENTENCE: a zip and a `.plist` are recognisable, and "not a
     * database" for everything else names the likeliest cause - an ENCRYPTED computer backup, whose
     * files are random bytes under the very same names.
     */
    public static HeadVerdict = (Head: Uint8Array, Name: string, TotalBytes: number): PlainHead => {
        const Out: PlainHead = new PlainHead();
        if (getCoreUtils().KErrorMode === true) { Out.Why = "WACommander cannot read a backup now."; return Out; }
        try {
            if (Head === null || Head === undefined || Head.length === 0) { Out.Why = ImportPlainRules.EmptyWhy; return Out; }
            if (ImportPlainRules.StartsWith(Head, [0x50, 0x4b, 0x03, 0x04]) === true) { Out.Why = ImportPlainRules.ZipWhy; return Out; }
            if (ImportPlainRules.StartsWith(Head, [0x62, 0x70, 0x6c, 0x69, 0x73, 0x74]) === true) { Out.Why = ImportPlainRules.PlistWhy; return Out; }
            if (Head.length < ImportPlainRules.HeadBytes || ImportPlainRules.StartsWith(Head, ImportPlainRules.Magic) === false) {
                Out.Why = ImportPlainRules.NotDatabaseWhy(Name);
                return Out;
            }
            const Size: number = ImportPlainRules.PageSizeIn(Head);
            // Bytes 56-59: the text encoding. 1 is UTF-8, which is what both phones write; anything else
            // is a database this page cannot search for table names.
            if (Size === 0 || Head[56] !== 0 || Head[57] !== 0 || Head[58] !== 0 || Head[59] !== 1) {
                Out.Why = ImportPlainRules.NotWhatsAppWhy;
                return Out;
            }
            if (ImportPlainRules.CutShort(Head, Size, TotalBytes) === true) { Out.Why = ImportPlainRules.TornWhy; return Out; }
            Out.Ok = true;
            Out.PageSize = Size;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportPlainRules.HeadVerdict");
            Out.Why = "That file could not be checked.";
            return Out;
        }
    };

    /** Bytes 16-17, big-endian; 1 stands for 65536. 0 when it is not a size SQLite uses.
     *  DOCUMENTED EXCEPTION: arithmetic on bytes in hand. ERROR-SUPPRESSED-JUSTIFIED: pure helper. */
    private static PageSizeIn = (Head: Uint8Array): number => {
        let Size: number = Head[16] * 256 + Head[17];
        if (Size === 1) { Size = 65536; }
        if (Size < 512 || Size > 65536) { return 0; }
        if ((Size & (Size - 1)) !== 0) { return 0; }
        return Size;
    };

    /**
     * Whether the file is SHORTER than the database says it is - a copy that was interrupted.
     * The header's own page count (bytes 28-31) is trusted only when SQLite says it is current
     * (bytes 92-95 equal the change counter at 24-27); an older writer leaves it stale, and then
     * nothing is concluded from it.
     * DOCUMENTED EXCEPTION: arithmetic on bytes in hand. ERROR-SUPPRESSED-JUSTIFIED: pure helper.
     */
    private static CutShort = (Head: Uint8Array, PageSize: number, TotalBytes: number): boolean => {
        for (let At: number = 0; At < 4; At = At + 1) {
            if (Head[24 + At] !== Head[92 + At]) { return false; }
        }
        const Pages: number = ((Head[28] * 256 + Head[29]) * 256 + Head[30]) * 256 + Head[31];
        if (Pages === 0) { return false; }
        return Pages * PageSize > Number(TotalBytes);
    };

    /** DOCUMENTED EXCEPTION: a byte comparison. ERROR-SUPPRESSED-JUSTIFIED: pure helper. */
    private static StartsWith = (Head: Uint8Array, Bytes: number[]): boolean => {
        if (Head.length < Bytes.length) { return false; }
        for (let At: number = 0; At < Bytes.length; At = At + 1) {
            if (Head[At] !== Bytes[At]) { return false; }
        }
        return true;
    };

    public static readonly ZipWhy: string = "That is a zip file. WACommander needs WhatsApp's database itself: from an"
        + " iPhone backup on a computer, the file named " + ImportPlainRules.HashedName + " - from an Android phone,"
        + " msgstore.db.crypt15. A chat exported from WhatsApp as a zip cannot be imported.";

    public static readonly PlistWhy: string = "That is one of the backup's own description files, not the WhatsApp"
        + " database. In the backup folder, open the folder named 7c and choose " + ImportPlainRules.HashedName + ".";

    public static readonly NotWhatsAppWhy: string = "That is a database, but not WhatsApp's - it has none of WhatsApp's"
        + " message tables. From an iPhone backup, open the folder named 7c and choose " + ImportPlainRules.HashedName + ".";

    public static readonly ManifestWhy: string = "That is the backup's index (Manifest.db), not the WhatsApp database."
        + " In the same backup folder, open the folder named 7c and choose " + ImportPlainRules.HashedName + ".";

    public static readonly TornWhy: string = "That database could not be read to the end - it looks cut short. Copy it"
        + " again from the backup folder and choose the new copy.";

    /**
     * ⚠ THE ENCRYPTED BACKUP IS NAMED, because it is the commonest cause and the one a subscriber
     * cannot see: with "Encrypt local backup" ticked, the file has the right name, the right size and
     * unreadable contents.
     */
    public static NotDatabaseWhy = (Name: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "That file cannot be used."; }
        try {
            let Lead: string = "That is not a WhatsApp database.";
            if (nz(Name).trim().toLowerCase() === ImportPlainRules.HashedName) {
                Lead = "That file has the right name, but it cannot be read as a database.";
            }
            return Lead + " If it came from an iPhone backup on a computer, the backup is probably encrypted: switch off"
                + " \"Encrypt local backup\" in Finder or iTunes (it asks for the backup's password), back up again, and choose "
                + ImportPlainRules.HashedName
                + " from the new backup. From an Android phone, choose msgstore.db.crypt15.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportPlainRules.NotDatabaseWhy");
            return "That file cannot be used.";
        }
    };

    /** What the page says under "Chosen: <name>" once the file is known to be usable. */
    public static ChosenWords = (Kind: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Kind === ImportPlainRules.Android) {
                return "This is an Android WhatsApp database that is already unlocked, so there is no key to type.";
            }
            return "This is an iPhone WhatsApp database. It is not locked with a key, so there is nothing to type.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "ImportPlainRules.ChosenWords");
            return "";
        }
    };

    /** The line under the import button. ⚠ True for this path: the file goes to their Dropbox, not through us. */
    public static readonly SendHint: string = "The file goes from this device straight to your own Dropbox."
        + " Photos and voice notes are not in it - only the messages.";

    /**
     * How to get the database out of an iPhone, in the order it is done.
     *
     * ⛔ "ENCRYPT LOCAL BACKUP" MUST BE OFF, AND IT IS SAID FIRST: an encrypted backup has the same
     * files under the same names, unreadable - the one mistake that costs a whole backup to undo.
     * ⚠ The folder locations are Apple's documented ones (macOS; iTunes from apple.com; the Microsoft
     * Store's iTunes / Apple Devices), WRITTEN FROM MEMORY OF APPLE'S SUPPORT PAGES - not re-read on
     * the day, and ⚠ UNVERIFIED ON A REAL BACKUP: there is none on the machine this was written on.
     * So the steps say what to look for ("one folder per device", "the folder named 7c") rather than
     * promise what will be there, and the last one gives a way round a file window that will not
     * open the backup folder (macOS guards it; whether a browser's file window may enter is unmeasured).
     * ⚠ Switching encryption OFF asks for the backup's password - said, or it reads as a dead end.
     */
    public static readonly IphoneSteps: string[] = [
        "Connect the iPhone to a computer. In Finder (Mac) or iTunes / Apple Devices (Windows), switch"
            + " \"Encrypt local backup\" off - it asks for the backup's password - and press Back Up Now."
            + " An encrypted backup cannot be read.",
        "Open the backup folder. Mac: ~/Library/Application Support/MobileSync/Backup. Windows:"
            + " %APPDATA%\\Apple Computer\\MobileSync\\Backup, or %USERPROFILE%\\Apple\\MobileSync\\Backup when iTunes"
            + " or Apple Devices came from the Microsoft Store. It holds one folder per device - open the one"
            + " changed most recently.",
        "Open the folder named 7c and choose the file " + ImportPlainRules.HashedName + " below. It has no file"
            + " type - that is WhatsApp's message database. If the file window will not open that folder, copy"
            + " the file to the Desktop first and choose the copy.",
    ];
}

/**
 * Reads a SQLite file's own table list - the `sqlite_master` b-tree, which starts on page 1 - and says
 * whose database it is. Fed one page at a time; knows nothing about where the bytes come from.
 *
 * The caller's loop: `Next()` names the page to read (0 = finished), `Take` is given its bytes.
 *
 * ⚠ HOW IT RECOGNISES A TABLE: a `sqlite_master` row stores `type`, `name`, `tbl_name` one after the
 * other, so the table `ZWAMESSAGE` is the bytes `tableZWAMESSAGEZWAMESSAGE` inside a leaf page. Only
 * pages of that tree are read (an interior page lists its children), so a message that merely
 * mentions a table name is never looked at.
 */
export class PlainSchemaWalk {
    /** A table list this long is not a phone's database; stop rather than read a whole file. */
    public static readonly MaxPages: number = 2000;

    /** What marks a table, per name this walk looks for. */
    public static readonly Marks: Record<string, string> = {
        iphone: "tableZWAMESSAGEZWAMESSAGE",
        droidMessage: "tablemessagemessage",
        droidChat: "tablechatchat",
        droidJid: "tablejidjid",
        manifest: "tableFilesFiles",
    };

    private PageSize: number;
    private TotalBytes: number;
    private Queue: number[] = [1];
    private Seen: Record<string, boolean> = { "1": true };
    private Read: number = 0;
    private Found: Record<string, boolean> = {};
    private Torn: boolean = false;

    public constructor(PageSize: number, TotalBytes: number) {
        this.PageSize = PageSize;
        this.TotalBytes = TotalBytes;
    }

    /** The next page to read (1-based), or 0 when the walk is over. */
    public Next = (): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (this.Torn === true || this.Queue.length === 0 || this.Read >= PlainSchemaWalk.MaxPages) { return 0; }
            return this.Queue[0];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "PlainSchemaWalk.Next");
            return 0;
        }
    };

    /** Where page `PageNo` starts in the file. DOCUMENTED EXCEPTION: arithmetic. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public OffsetOf = (PageNo: number): number => {
        return (PageNo - 1) * this.PageSize;
    };

    /** DOCUMENTED EXCEPTION: returns a field. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public PageBytes = (): number => {
        return this.PageSize;
    };

    /** One page's bytes. A short or absent page means the file is cut short. */
    public Take = (PageNo: number, Bytes: Uint8Array | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Queue.length === 0 || this.Queue[0] !== PageNo) { return; }
            this.Queue.shift();
            this.Read = this.Read + 1;
            if (Bytes === null || Bytes.length < this.PageSize) { this.Torn = true; return; }
            let At: number = 0;
            if (PageNo === 1) { At = ImportPlainRules.HeadBytes; }
            const Kind: number = Bytes[At];
            if (Kind === 0x0d) { this.NoteMarks(Bytes); return; }
            if (Kind !== 0x05) { this.Torn = true; return; }
            this.NoteChildren(Bytes, At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "PlainSchemaWalk.Take");
            this.Torn = true;
        }
    };

    /** An interior page: each cell begins with its left child's page number; the header holds the right-most. */
    private NoteChildren = (Bytes: Uint8Array, At: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Cells: number = Bytes[At + 3] * 256 + Bytes[At + 4];
            this.Enqueue(PlainSchemaWalk.U32(Bytes, At + 8));
            for (let Cell: number = 0; Cell < Cells; Cell = Cell + 1) {
                const Pointer: number = At + 12 + Cell * 2;
                if (Pointer + 1 >= Bytes.length) { this.Torn = true; return; }
                const Where: number = Bytes[Pointer] * 256 + Bytes[Pointer + 1];
                if (Where + 3 >= Bytes.length) { this.Torn = true; return; }
                this.Enqueue(PlainSchemaWalk.U32(Bytes, Where));
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "PlainSchemaWalk.NoteChildren");
            this.Torn = true;
        }
    };

    private Enqueue = (PageNo: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⛔ A PAGE PAST THE END OF THE FILE means the file is cut short (or is not what it claims).
            if (PageNo < 1 || this.OffsetOf(PageNo) + this.PageSize > this.TotalBytes) { this.Torn = true; return; }
            if (this.Seen[String(PageNo)] === true) { return; }
            this.Seen[String(PageNo)] = true;
            this.Queue.push(PageNo);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "PlainSchemaWalk.Enqueue");
        }
    };

    /** DOCUMENTED EXCEPTION: four bytes, big-endian. ERROR-SUPPRESSED-JUSTIFIED: pure helper. */
    private static U32 = (Bytes: Uint8Array, At: number): number => {
        return ((Bytes[At] * 256 + Bytes[At + 1]) * 256 + Bytes[At + 2]) * 256 + Bytes[At + 3];
    };

    /** A leaf page: which of the names this walk looks for are tables listed on it. */
    private NoteMarks = (Bytes: Uint8Array): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            let Text: string = "";
            for (let At: number = 0; At < Bytes.length; At = At + 4096) {
                Text = Text + String.fromCharCode.apply(null, Array.from(Bytes.subarray(At, At + 4096)));
            }
            for (const Name of Object.keys(PlainSchemaWalk.Marks)) {
                if (Text.indexOf(PlainSchemaWalk.Marks[Name]) >= 0) { this.Found[Name] = true; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "PlainSchemaWalk.NoteMarks");
        }
    };

    /**
     * Whose database it is. ⚠ An iPhone's wins over everything; an Android one needs all three of its
     * core tables; the backup's own index gets its own sentence; a cut-short file says so.
     */
    public Verdict = (): PlainVerdict => {
        const Out: PlainVerdict = new PlainVerdict();
        if (getCoreUtils().KErrorMode === true) { Out.Why = "WACommander cannot read a backup now."; return Out; }
        try {
            if (this.Found.iphone === true) { Out.Ok = true; Out.Kind = ImportPlainRules.Iphone; return Out; }
            if (this.Found.droidMessage === true && this.Found.droidChat === true && this.Found.droidJid === true) {
                Out.Ok = true;
                Out.Kind = ImportPlainRules.Android;
                return Out;
            }
            if (this.Torn === true) { Out.Why = ImportPlainRules.TornWhy; return Out; }
            if (this.Found.manifest === true) { Out.Why = ImportPlainRules.ManifestWhy; return Out; }
            Out.Why = ImportPlainRules.NotWhatsAppWhy;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "PlainSchemaWalk.Verdict");
            Out.Why = "That file could not be checked.";
            return Out;
        }
    };
}
